import type { MarketProfileState } from './marketProfileStore';
import { computeProgress, isQuestionnaireSubmitted } from './marketProfileStore';

export type MarketProfileNavTarget = 'overview' | 'position' | 'grading' | 'marketData';

export type MarketProfileCtaKind =
  | 'empty'
  | 'position_done'
  | 'grading_done'
  | 'complete'
  | 'outdated'
  | 'pending_review';

export type MarketProfileCta = {
  kind: MarketProfileCtaKind;
  badge: string;
  badgeOpen: boolean;
  badgeUpdate: boolean;
  title: string;
  description: string;
  action: string;
  navTarget: MarketProfileNavTarget;
  primaryTarget: MarketProfileNavTarget;
  progressSubmitted: number;
  progressPercent: number;
  progressLabel: string;
  showFillPrompt: boolean;
};

/** Следующий шаг для основной кнопки на странице профиля */
export function resolvePrimaryNavTarget(state: MarketProfileState): MarketProfileNavTarget {
  const progress = computeProgress(state);
  const positionDone = isQuestionnaireSubmitted(state.position.status);
  const gradingDone = isQuestionnaireSubmitted(state.grading.status);

  if (progress.completed === 2) return 'marketData';
  if (positionDone && !gradingDone) return 'grading';
  if (gradingDone && !positionDone) return 'position';
  return 'position';
}

export function resolveMarketProfileCta(state: MarketProfileState): MarketProfileCta {
  const progress = computeProgress(state);
  const positionDone = isQuestionnaireSubmitted(state.position.status);
  const gradingDone = isQuestionnaireSubmitted(state.grading.status);
  const primaryTarget = resolvePrimaryNavTarget(state);

  const progressSubmitted = progress.submitted;
  const progressPercent =
    progress.completed === 2
      ? 100
      : (progressSubmitted / progress.total) * 100;

  const progressLabel =
    progress.completed === 2
      ? '2 из 2 анкет проверены'
      : progressSubmitted > progress.completed
        ? `${progressSubmitted} из ${progress.total} анкет отправлено · ${progress.completed} проверено`
        : `${progressSubmitted} из ${progress.total} анкет заполнено`;

  if (progress.outdated) {
    return {
      kind: 'outdated',
      badge: 'Обновить',
      badgeOpen: false,
      badgeUpdate: true,
      title: 'Обновите рыночный профиль',
      description: 'Для актуального сравнения',
      action: 'К рыночным данным →',
      navTarget: 'marketData',
      primaryTarget: progress.completed === 2 ? 'marketData' : 'overview',
      progressSubmitted: 2,
      progressPercent,
      progressLabel: '2 из 2 анкет · требуется обновление',
      showFillPrompt: false,
    };
  }

  if (progress.completed === 2) {
    return {
      kind: 'complete',
      badge: 'Открыто',
      badgeOpen: true,
      badgeUpdate: false,
      title: 'Ваше сравнение с рынком готово',
      description: 'Компенсация и уровень роли',
      action: 'Перейти к рыночным данным →',
      navTarget: 'marketData',
      primaryTarget,
      progressSubmitted: 2,
      progressPercent,
      progressLabel: '2 из 2 анкет проверены',
      showFillPrompt: false,
    };
  }

  if (progress.submitted === 2 && progress.completed < 2) {
    return {
      kind: 'pending_review',
      badge: '2/2',
      badgeOpen: false,
      badgeUpdate: false,
      title: 'Анкеты на проверке',
      description: 'Обычно до 24 часов',
      action: 'К профилю →',
      navTarget: 'overview',
      primaryTarget: 'overview',
      progressSubmitted: 2,
      progressPercent,
      progressLabel,
      showFillPrompt: false,
    };
  }

  if (positionDone && !gradingDone) {
    return {
      kind: 'position_done',
      badge: '1/2',
      badgeOpen: false,
      badgeUpdate: false,
      title: 'Остался один шаг',
      description: 'Пройдите грейдинг роли',
      action: 'Завершить профиль →',
      navTarget: 'grading',
      primaryTarget,
      progressSubmitted: 1,
      progressPercent,
      progressLabel: '1 из 2 анкет заполнено',
      showFillPrompt: true,
    };
  }

  if (gradingDone && !positionDone) {
    return {
      kind: 'grading_done',
      badge: '1/2',
      badgeOpen: false,
      badgeUpdate: false,
      title: 'Добавьте данные о компенсации',
      description: 'Чтобы сравнить пакет с рынком',
      action: 'Завершить профиль →',
      navTarget: 'position',
      primaryTarget,
      progressSubmitted: 1,
      progressPercent,
      progressLabel: '1 из 2 анкет заполнено',
      showFillPrompt: true,
    };
  }

  return {
    kind: 'empty',
    badge: '0/2',
    badgeOpen: false,
    badgeUpdate: false,
    title: 'Узнайте, в рынке ли ваша компенсация',
    description: 'Создайте рыночный профиль',
    action: 'Сравнить себя с рынком →',
    navTarget: 'overview',
    primaryTarget,
    progressSubmitted: 0,
    progressPercent,
    progressLabel: '0 из 2 анкет заполнено',
    showFillPrompt: true,
  };
}

export function navTargetToProfileScreen(
  target: MarketProfileNavTarget,
): 'overview' | 'position' | 'grading' | 'marketData' {
  return target;
}
